// FILE: src/pages/AdminResourceEditPage.jsx
// Add or edit a lesson's details on the real schema (WP-5; R1, matrix 9.7,
// 0.9, 3.7; B-13): curriculum and type (fixed once created), kind (content
// or graded assessment), title, description, position (unique per
// curriculum and type → RESOURCE_ORDER_TAKEN), content file, parent chapter
// (homelinks, D2; assessments, D3), practice flag (untracked, never gates),
// pass mark (entered as %, stored as a fraction) and learning outcomes (CC
// codes). Slides and videos have their own page (AdminResourceSlidesPage).
//
// Problems — found here first, or sent back by the server with its field
// paths — are listed at the top (each linked to its field) and shown under
// the field. A refused save keeps the form (B-13).
//
//   /admin/resources/new[?curriculumId=]   create (POST)  → the lesson's page
//   /admin/resources/:id/edit              edit   (PATCH) → stays, "Lesson saved."
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  MenuItem,
  Stack,
  TextField
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewCarouselOutlinedIcon from "@mui/icons-material/ViewCarouselOutlined";
import fetchWithAuth from "../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { ProblemSummary } from "../components/admin/ContentBits";
import { formatDateTime } from "../components/admin/format";
import {
  curriculumName,
  detailsForm,
  detailsPayload,
  hasParent,
  problemList,
  slideCodes,
  validateDetails
} from "../components/admin/content";
import { errorPayload } from "../components/admin/apiError";
import { formatDuration } from "../utils/timeFormat";
import { errorCode, errorMessage, messageFor } from "../i18n/errors";
import { resourceKindLabel, resourceTypeLabel } from "../i18n/labels";

/** Server field path → the input it belongs to. */
function inputFor(field) {
  if (/^ilos(\[|$)/.test(field)) return "ilos";
  if (field === "assessment" || field === "assessment.passMark") return "passMark";
  return field;
}

/** A refused save → problems in the server's shape (field paths), when it named fields. */
function refusalProblems(err) {
  const body = errorPayload(err);
  const code = errorCode(err);
  if (code === "VALIDATION_FAILED" && Array.isArray(body.problems)) return body.problems;
  if (code === "MISSING_REQUIRED_FIELDS" && Array.isArray(body.required)) {
    return body.required.map((field) => ({ field, problem: "required" }));
  }
  if (code === "RESOURCE_ORDER_TAKEN") return [{ field: "order", problem: "taken" }];
  return [];
}

export default function AdminResourceEditPage({ mode = "edit" }) {
  const create = mode === "create";
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [resource, setResource] = useState(null);
  const [loadState, setLoadState] = useState(create ? "done" : "loading");
  const [loadError, setLoadError] = useState(null);
  const [curricula, setCurricula] = useState(null); // create: the list to choose from
  const [chapters, setChapters] = useState([]); // parent chapter choices
  const [form, setForm] = useState(() => detailsForm(null, { curriculumId: params.get("curriculumId") || "" }));
  const [problems, setProblems] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [orderTakenBy, setOrderTakenBy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const summaryRef = useRef(null);
  const alertRef = useRef(null);

  // Edit: always the current version (also after a reload).
  const load = useCallback(async () => {
    if (create) return;
    setLoadState((s) => (s === "done" ? "done" : "loading"));
    setLoadError(null);
    try {
      const r = await fetchWithAuth(`/api/admin/resources/${encodeURIComponent(id)}`);
      setResource(r);
      setForm(detailsForm(r));
      setLoadState("done");
    } catch (err) {
      setLoadError(err || "INTERNAL_ERROR");
      setLoadState("failed");
    }
  }, [create, id]);

  useEffect(() => {
    load();
  }, [load]);

  // Create: the curricula a lesson can go into (drafts only).
  useEffect(() => {
    if (!create) return;
    fetchWithAuth("/api/admin/curricula")
      .then((list) => setCurricula(Array.isArray(list) ? list : []))
      .catch(() => setCurricula([]));
  }, [create]);

  // Parent chapter choices: content chapters of the same curriculum.
  const curriculumKey = create ? form.curriculumId || "none" : resource ? String(resource.curriculumId || "none") : null;
  const wantsParent = hasParent(form);
  useEffect(() => {
    if (!wantsParent || !curriculumKey) return;
    let live = true;
    fetchWithAuth(`/api/admin/resources?type=CHAPTER&curriculumId=${encodeURIComponent(curriculumKey)}`)
      .then((list) => live && setChapters(Array.isArray(list) ? list : []))
      .catch(() => live && setChapters([]));
    return () => {
      live = false;
    };
  }, [wantsParent, curriculumKey]);

  const fieldErrors = {};
  for (const it of problemList(problems)) {
    const key = inputFor(it.field);
    if (!fieldErrors[key]) fieldErrors[key] = it.message;
  }
  if (orderTakenBy) {
    fieldErrors.order = `Position ${form.order} is already used by "${orderTakenBy}". Choose a different position.`;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "resourceType") next.kind = value === "HOMELINK" ? "HOMELINK" : "CONTENT";
      return next;
    });
    setSaved(false);
    if (name === "order") setOrderTakenBy(null);
    setProblems((list) => list.filter((p) => inputFor(p.field) !== name));
  };

  const drafts = (curricula || []).filter((c) => c.status === "DRAFT");
  const needsCurriculum = create && (curricula || []).length > 0;
  const locked = !create && resource?.curriculum && resource.curriculum.status !== "DRAFT";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    setOrderTakenBy(null);
    const found = validateDetails(form, { create, needsCurriculum });
    setProblems(found);
    if (found.length) {
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setSubmitting(true);
    try {
      const body = detailsPayload(form, { create });
      if (create) {
        const created = await fetchWithAuth("/api/admin/resources", { method: "POST", body: JSON.stringify(body) });
        navigate(`/admin/resources/${created._id}`, {
          state: { notice: `${created.title || "The lesson"} was added. Add its slides and videos next.` }
        });
        return;
      }
      const updated = await fetchWithAuth(`/api/admin/resources/${encodeURIComponent(resource._id)}`, {
        method: "PATCH",
        body: JSON.stringify(body)
      });
      setResource((prev) => ({ ...prev, ...updated }));
      setSaved(true);
    } catch (err) {
      // B-13: say why, keep the form.
      const named = refusalProblems(err);
      setProblems(named);
      if (errorCode(err) === "RESOURCE_ORDER_TAKEN") setOrderTakenBy(errorPayload(err).takenBy?.title || "another lesson");
      setSaveError(err || "INTERNAL_ERROR");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const title = resource?.title || "lesson";
  const breadcrumbs = create
    ? [{ label: "Lessons", to: "/admin/resources" }, { label: "Add a lesson" }]
    : resource
      ? [
          { label: "Lessons", to: "/admin/resources" },
          { label: resource.title || "Lesson", to: `/admin/resources/${resource._id}` },
          { label: "Edit details" }
        ]
      : [{ label: "Lessons", to: "/admin/resources" }, { label: "Edit lesson" }];

  if (!create && !resource) {
    if (loadState === "loading") return <LoadingState variant="page" label="Loading the lesson…" />;
    const notFound = errorCode(loadError) === "RESOURCE_NOT_FOUND";
    return (
      <Box>
        <PageHeader title="Edit lesson" breadcrumbs={breadcrumbs} />
        {notFound || !loadError ? (
          <EmptyState
            title={messageFor("RESOURCE_NOT_FOUND")}
            description="It may have been removed. Go back to the lesson list and choose another."
            action={
              <Button component={RouterLink} to="/admin/resources" variant="contained">
                Back to lessons
              </Button>
            }
          />
        ) : (
          <ErrorState error={loadError} title="We couldn't load this lesson" onRetry={load} />
        )}
      </Box>
    );
  }

  if (create && curricula === null) return <LoadingState variant="page" label="Loading the curricula…" />;

  const summaryItems = problemList(problems);
  const clientProblems = summaryItems.length > 0 && !saveError;
  const fromSlides = slideCodes(resource?.slides);
  const parentChoices = chapters.filter((c) => c.kind !== "ASSESSMENT" && String(c._id) !== String(resource?._id || ""));
  const slides = resource?.totalSlides || (Array.isArray(resource?.slides) ? resource.slides.length : 0);
  const curriculumId = resource?.curriculum?.id || resource?.curriculumId;

  return (
    <Box>
      <PageHeader
        title={create ? "Add a lesson" : `Edit ${title}`}
        breadcrumbs={breadcrumbs}
        description={
          create
            ? "Add a chapter or homelink to a draft curriculum. You can add its slides and videos on the next page."
            : "Change what this lesson is, where it sits in the course and how it's assessed."
        }
      />

      {create && needsCurriculum && drafts.length === 0 && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          <AlertTitle>No draft curriculum</AlertTitle>
          Lessons can only be added to a draft curriculum. Make a new version of an approved curriculum on the{" "}
          <Link component={RouterLink} to="/admin/curricula">
            Curricula
          </Link>{" "}
          page first.
        </Alert>
      )}
      {locked && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          <AlertTitle>This lesson can't be changed</AlertTitle>
          It belongs to {curriculumName(resource.curriculum)}, which is {resource.curriculum.status === "RETIRED" ? "retired" : "approved"}.
          To change it, make a new version on the{" "}
          <Link component={RouterLink} to={`/admin/curricula/${resource.curriculum.id}`}>
            curriculum's page
          </Link>
          .
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: create ? "minmax(0, 1fr)" : "minmax(0, 1.6fr) minmax(0, 1fr)" }
        }}
      >
        <Box component="form" noValidate onSubmit={handleSubmit} aria-label={create ? "Add a lesson" : "Edit lesson"} sx={{ minWidth: 0 }}>
          <Stack spacing={2.5}>
            {clientProblems && (
              <ProblemSummary ref={summaryRef} title="Check the lesson details" items={summaryItems} idFor={inputFor} />
            )}
            {saveError && (
              <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
                <AlertTitle>We couldn't save the lesson</AlertTitle>
                {errorMessage(saveError)} Your changes are still in the form below.
                {summaryItems.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                    {summaryItems.map((it) => (
                      <li key={it.field}>
                        <strong>{it.label}</strong>: {it.field === "order" && orderTakenBy ? fieldErrors.order : it.message}
                      </li>
                    ))}
                  </Box>
                )}
                {errorCode(saveError) === "CURRICULUM_LOCKED" && curriculumId && (
                  <Box sx={{ mt: 1 }}>
                    <Link component={RouterLink} to={`/admin/curricula/${curriculumId}`} color="inherit">
                      Open the curriculum
                    </Link>
                  </Box>
                )}
              </Alert>
            )}
            {saved && (
              <Alert severity="success" role="status">
                Lesson saved.
              </Alert>
            )}

            <Section id="lesson-place" title="Where it belongs">
              <Stack spacing={2.5}>
                {create ? (
                  <>
                    {needsCurriculum && (
                      <TextField
                        select
                        id="curriculumId"
                        name="curriculumId"
                        label="Curriculum"
                        value={drafts.some((c) => c.id === form.curriculumId) ? form.curriculumId : ""}
                        onChange={handleChange}
                        error={!!fieldErrors.curriculumId}
                        helperText={fieldErrors.curriculumId || "Only draft curricula can take new lessons."}
                        required
                        fullWidth
                      >
                        {drafts.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {curriculumName(c)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    <TextField
                      select
                      id="resourceType"
                      name="resourceType"
                      label="Type"
                      value={form.resourceType}
                      onChange={handleChange}
                      error={!!fieldErrors.resourceType}
                      helperText={fieldErrors.resourceType || "Chapters are the core (in-class) lessons; homelinks are flexible-hour activities."}
                      required
                      fullWidth
                    >
                      <MenuItem value="CHAPTER">{resourceTypeLabel("CHAPTER")}</MenuItem>
                      <MenuItem value="HOMELINK">{resourceTypeLabel("HOMELINK")}</MenuItem>
                    </TextField>
                  </>
                ) : (
                  <DetailList
                    items={[
                      { label: "Curriculum", value: resource.curriculum ? curriculumName(resource.curriculum) : "Not in a curriculum" },
                      { label: "Type", value: resourceTypeLabel(resource.resourceType) }
                    ]}
                  />
                )}
                {form.resourceType === "CHAPTER" && (
                  <TextField
                    select
                    id="kind"
                    name="kind"
                    label="Kind of chapter"
                    value={form.kind === "ASSESSMENT" ? "ASSESSMENT" : "CONTENT"}
                    onChange={handleChange}
                    error={!!fieldErrors.kind}
                    helperText={fieldErrors.kind || "A graded assessment is launched on its own after its chapter's content."}
                    fullWidth
                  >
                    <MenuItem value="CONTENT">{resourceKindLabel("CONTENT")}</MenuItem>
                    <MenuItem value="ASSESSMENT">{resourceKindLabel("ASSESSMENT")}</MenuItem>
                  </TextField>
                )}
                {wantsParent && (
                  <TextField
                    select
                    id="parentResourceId"
                    name="parentResourceId"
                    label="Parent chapter"
                    value={parentChoices.some((c) => String(c._id) === form.parentResourceId) ? form.parentResourceId : ""}
                    onChange={handleChange}
                    error={!!fieldErrors.parentResourceId}
                    helperText={
                      fieldErrors.parentResourceId ||
                      (form.resourceType === "HOMELINK"
                        ? "The homelink opens when this chapter is finished. With no parent chapter it is open from the start."
                        : "The chapter this assessment tests.")
                    }
                    slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                    fullWidth
                  >
                    <MenuItem value="">No parent chapter</MenuItem>
                    {parentChoices.map((c) => (
                      <MenuItem key={c._id} value={String(c._id)}>
                        {`${c.order ?? "—"}. ${c.title}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  id="order"
                  name="order"
                  label="Position in the course"
                  value={form.order}
                  onChange={handleChange}
                  error={!!fieldErrors.order}
                  helperText={
                    fieldErrors.order ||
                    "Lessons of the same type open in this order (1 first). Each position is used once per curriculum."
                  }
                  slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*" } }}
                  required
                  sx={{ maxWidth: { sm: 360 } }}
                />
              </Stack>
            </Section>

            <Section id="lesson-details" title="Lesson details">
              <Stack spacing={2.5}>
                <TextField
                  id="title"
                  name="title"
                  label="Title"
                  value={form.title}
                  onChange={handleChange}
                  error={!!fieldErrors.title}
                  helperText={fieldErrors.title || "Students see this in their lesson list."}
                  required
                  fullWidth
                />
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  value={form.description}
                  onChange={handleChange}
                  error={!!fieldErrors.description}
                  helperText={fieldErrors.description || "Optional. One or two sentences about what the lesson covers."}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <TextField
                  id="url"
                  name="url"
                  label="Content file"
                  value={form.url}
                  onChange={handleChange}
                  error={!!fieldErrors.url}
                  helperText={fieldErrors.url || "Where the lesson's files are in the content store, like chapters/ch_1/story.html."}
                  slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: "none" } }}
                  fullWidth
                />
              </Stack>
            </Section>

            <Section id="lesson-assessment" title="Scoring and learning outcomes">
              <Stack spacing={2.5}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isPractice"
                        name="isPractice"
                        checked={form.isPractice}
                        onChange={handleChange}
                        inputProps={{ "aria-describedby": "isPractice-help" }}
                      />
                    }
                    label="Practice activity"
                  />
                  <FormHelperText id="isPractice-help" error={!!fieldErrors.isPractice} sx={{ mt: 0 }}>
                    {fieldErrors.isPractice ||
                      "Practice is never scored, never blocks the next chapter and doesn't count toward the core hours."}
                  </FormHelperText>
                </Box>
                <TextField
                  id="passMark"
                  name="passMark"
                  label="Pass mark (%)"
                  value={form.isPractice ? "" : form.passMark}
                  onChange={handleChange}
                  disabled={form.isPractice}
                  error={!!fieldErrors.passMark}
                  helperText={
                    fieldErrors.passMark ||
                    (form.isPractice
                      ? "Practice activities aren't scored."
                      : "The score needed to pass, for example 80. Leave it empty if this lesson has no graded quiz.")
                  }
                  slotProps={{ htmlInput: { inputMode: "decimal" } }}
                  sx={{ maxWidth: { sm: 360 } }}
                />
                <TextField
                  id="ilos"
                  name="ilos"
                  label="Other learning outcomes (CC codes)"
                  value={form.ilos}
                  onChange={handleChange}
                  error={!!fieldErrors.ilos}
                  helperText={
                    fieldErrors.ilos ||
                    `Separate codes with commas, like U1.L2.ILO3. ${
                      fromSlides.length ? `Codes on the slides are added for you: ${fromSlides.join(", ")}.` : "Codes on the slides are added for you."
                    }`
                  }
                  slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: "characters" } }}
                  fullWidth
                />
              </Stack>
            </Section>

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              <Button component={RouterLink} to={create ? "/admin/resources" : `/admin/resources/${resource._id}`} variant="outlined">
                {create ? "Back to lessons" : "Back to the lesson"}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                disabled={submitting || locked || (create && needsCurriculum && drafts.length === 0)}
              >
                {submitting ? "Saving…" : create ? "Add lesson" : "Save lesson"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {!create && (
          <Section id="lesson-content" title="Slides and videos" description="Edited on their own page.">
            <DetailList
              columns={1}
              items={[
                { label: "Type", value: resourceTypeLabel(resource.resourceType) },
                { label: "Slides", value: String(slides) },
                { label: "Total duration", value: resource.totalDuration ? formatDuration(resource.totalDuration) : "—" },
                { label: "Activities", value: resource.totalChallenges ? String(resource.totalChallenges) : "None" },
                { label: "Videos", value: String((resource.media || []).length || "None") },
                { label: "Last changed", value: formatDateTime(resource.updatedAt) }
              ]}
            />
            <Button
              component={RouterLink}
              to={`/admin/resources/${resource._id}/slides`}
              variant="outlined"
              startIcon={<ViewCarouselOutlinedIcon />}
              sx={{ mt: 2.5 }}
            >
              Edit slides and videos
            </Button>
          </Section>
        )}
      </Box>
    </Box>
  );
}
